<script setup lang="ts">
/**
 * On Board — Corporate Secretarial / Nominee Director / Registered Address service bundle.
 *   등록 템플릿(원본 번들)의 운영자 입력 구간만 문서형으로 재현:
 *   계약일 · 수수료 · 서비스 선택 · 메일 핸들링 · 등록부 위치 체크박스 ·
 *   당사자(Company director / Principal(UBO) / Service Provider(Aura)) + 증인 ·
 *   Schedule 1 반복부(directors/ubos/shareholders) · Authorised Person(s) [[Signers]].
 *   지명 서명필드([[*_signature]])는 T-Sign 단계에서 채워지므로 화면 입력 없음(normalize 가 빈값 방출).
 */
import { inject } from 'vue';
import CddVarField from '../cdd/CddVarField.vue';
import AgmSigners from '../cdd/AgmSigners.vue';
import OnBoardLoopBlock from './OnBoardLoopBlock.vue';
import { useAgmFill, DOC_SCOPE } from '../cdd/useCddFill';
import './onboardDoc.css';

const fill = useAgmFill();
const scope = inject(DOC_SCOPE, '');
const K = (k: string) => scope + k;

function isOn(k: string): boolean {
  const v = String(fill.getValue(K(k)) || '').toLowerCase();
  return v === 'yes' || v === 'true' || v === '1';
}
function toggle(k: string) { fill.setValue(K(k), isOn(k) ? 'no' : 'yes'); }
function selLoc(k: string, val: string): boolean { return fill.getValue(K(k)) === val; }
function pickLoc(k: string, val: string) { fill.setValue(K(k), selLoc(k, val) ? '' : val); }

const SERVICES = [
  { key: 'service_secretarial', label: 'Standard Annual Secretarial Services' },
  { key: 'service_registered_office', label: 'Provision of Registered Office' },
  { key: 'service_nominee_director', label: 'Provision of Nominee Director' },
  { key: 'service_nominee_shareholder', label: 'Provision of Nominee Shareholder' },
];
const MAIL = [
  { key: 'mail_keep', label: 'Keep for collection' },
  { key: 'mail_forward', label: 'Forward to below address' },
  { key: 'mail_scan', label: 'Scan & email' },
];
const REGISTERS = [
  { key: 'roc_location', label: 'Register of Registrable Controllers' },
  { key: 'rond_location', label: 'Register of Nominee Directors' },
  { key: 'rons_location', label: 'Register of Nominee Shareholders' },
];
const DIR_COLS = [
  { key: 'director_name', label: 'Name' },
  { key: 'director_identification_no', label: 'ID / Passport No.' },
  { key: 'director_address', label: 'Address' },
  { key: 'director_mobile_no', label: 'Contact No.' },
  { key: 'director_email', label: 'Email' },
];
const UBO_COLS = [
  { key: 'ubo_name', label: 'Name' },
  { key: 'ubo_identification_no', label: 'ID / Passport No.' },
  { key: 'ubo_residential_address', label: 'Address' },
];
const SH_COLS = [
  { key: 'shareholder_name', label: 'Name' },
  { key: 'shareholder_identification_no', label: 'ID / Passport No.' },
  { key: 'shareholder_address', label: 'Address' },
];
const FEES = [
  { key: 'fee_nominee_secretary_annual', label: 'Nominee Secretary (annual)' },
  { key: 'fee_secretarial_annual', label: 'Corporate Secretarial (annual)' },
  { key: 'fee_nominee_director_annual', label: 'Nominee Director (annual)' },
  { key: 'fee_nominee_director_deposit', label: 'Nominee Director (security deposit)' },
  { key: 'fee_registered_address_annual', label: 'Registered Address (annual)' },
  // 고객 수정본(2026-10): Schedule 2 요금이 active/dormant 구분 없이 한 줄("From S$… per annum")로 바뀜 → dormant 칸 삭제.
  { key: 'fee_compilation_active', label: 'Compilation report & corporate tax (from)' },
];
</script>

<template>
  <div class="agm-doc">
    <div class="doc-head">
      <div class="doc-title"><CddVarField field-key="company_name" label="Company Name" /></div>
      <div class="doc-sub">Company Registration No. <CddVarField field-key="company_registration_no" label="Reg. No." /></div>
      <div class="doc-sub">Registered office: <CddVarField field-key="registered_office_address" label="Registered Office Address" /></div>
    </div>

    <div class="sec-title">Corporate Secretarial Service Agreement</div>
    <p class="doc-p">This Agreement is made on <CddVarField field-key="agreement_date" label="Agreement Date" /> between the Company and AURA PARTNERS (SINGAPORE) PTE. LTD.</p>

    <div class="sec-title">Fees</div>
    <div class="doc-p doc-note">Enter the amount only (e.g. 2,400). The template already adds the currency and &ldquo;/-&rdquo;.</div>
    <table class="cdd-tbl">
      <tbody>
        <tr v-for="f in FEES" :key="f.key">
          <td class="cdd-lcell">{{ f.label }}</td>
          <td><CddVarField :field-key="f.key" :label="f.label" /></td>
        </tr>
      </tbody>
    </table>

    <div class="sec-title">Standard service selection</div>
    <div class="doc-p doc-note">(Mark the services the Company engages.)</div>
    <div class="cb-grid">
      <div v-for="s in SERVICES" :key="s.key" class="cb-row doc-toggle" @click="toggle(s.key)">
        <span class="cb-mark">{{ isOn(s.key) ? '☒' : '☐' }}</span> {{ s.label }}
      </div>
    </div>

    <div class="sec-title">Statutory register locations</div>
    <div class="doc-p doc-note">(Where each register is kept.)</div>
    <div v-for="r in REGISTERS" :key="r.key" class="reg-row">
      <div class="reg-lbl">{{ r.label }}</div>
      <span class="cb-row doc-toggle" @click="pickLoc(r.key, 'company')">
        <span class="cb-mark">{{ selLoc(r.key, 'company') ? '☒' : '☐' }}</span> Registered office of the company
      </span>
      <span class="cb-row doc-toggle" @click="pickLoc(r.key, 'csp')">
        <span class="cb-mark">{{ selLoc(r.key, 'csp') ? '☒' : '☐' }}</span> Registered office of the corporate service provider
      </span>
    </div>

    <div class="sec-title">Registered address &mdash; mail handling</div>
    <div class="cb-grid">
      <div v-for="m in MAIL" :key="m.key" class="cb-row doc-toggle" @click="toggle(m.key)">
        <span class="cb-mark">{{ isOn(m.key) ? '☒' : '☐' }}</span> {{ m.label }}
      </div>
    </div>
    <table class="cdd-tbl">
      <tbody>
        <tr><td class="cdd-lcell">Contact for mail notification</td><td><CddVarField field-key="mail_contact" label="Mail Contact" /></td></tr>
        <tr><td class="cdd-lcell">Correspondence address for forwarding</td><td><CddVarField field-key="mail_forward_address" label="Forwarding Address" /></td></tr>
      </tbody>
    </table>

    <div class="sec-title">Nominee Resident Director Agreement &mdash; Principal</div>
    <table class="cdd-tbl">
      <tbody>
        <tr><td class="cdd-lcell">Principal (UBO) Name</td><td><CddVarField field-key="ubo_name" label="Principal Name" /></td></tr>
        <tr><td class="cdd-lcell">Passport / ID No.</td><td><CddVarField field-key="ubo_identification_no" label="Principal ID" /></td></tr>
        <tr><td class="cdd-lcell">Residential Address</td><td><CddVarField field-key="ubo_residential_address" label="Principal Address" /></td></tr>
      </tbody>
    </table>

    <div class="sec-title">Execution</div>
    <table class="cdd-tbl">
      <tbody>
        <tr><td class="cdd-lcell">The Company &mdash; Director</td><td><CddVarField field-key="director_name" label="Director Name" /></td></tr>
        <tr><td class="cdd-lcell">Director Designation</td><td><CddVarField field-key="director_designation" label="Designation" /></td></tr>
        <tr><td class="cdd-lcell">Witness (Company) Name</td><td><CddVarField field-key="company_witness_name" label="Witness Name" /></td></tr>
        <tr><td class="cdd-lcell">Witness (Company) ID</td><td><CddVarField field-key="company_witness_identification_no" label="Witness ID" /></td></tr>
        <tr><td class="cdd-lcell">Witness (Principal) Name</td><td><CddVarField field-key="principal_witness_name" label="Witness Name" /></td></tr>
        <tr><td class="cdd-lcell">Witness (Principal) ID</td><td><CddVarField field-key="principal_witness_identification_no" label="Witness ID" /></td></tr>
        <tr><td class="cdd-lcell">Service Provider &mdash; Aura Signatory</td><td><CddVarField field-key="aura_signatory_name" label="Aura Signatory" /></td></tr>
        <tr><td class="cdd-lcell">Aura Signatory Designation</td><td><CddVarField field-key="aura_signatory_designation" label="Designation" /></td></tr>
        <tr><td class="cdd-lcell">Witness (Service Provider) Name</td><td><CddVarField field-key="serviceprovider_witness_name" label="Witness Name" /></td></tr>
        <tr><td class="cdd-lcell">Witness (Service Provider) ID</td><td><CddVarField field-key="serviceprovider_witness_identification_no" label="Witness ID" /></td></tr>
        <tr><td class="cdd-lcell">Dated</td><td><CddVarField field-key="dated_on" label="Date" /></td></tr>
      </tbody>
    </table>

    <!-- Letter of Indemnity "WITNESSED BY" — 템플릿에 [[witness_name]]/[[witness_identification_no]]/[[witness_date]] 가
         있는데 입력칸이 없어 빈칸으로 나갔다. (Form 45 의 witness_* 는 f45_ 스코프 소유라 섞이지 않음) -->
    <div class="sec-title">Letter of Indemnity &mdash; Witness</div>
    <table class="cdd-tbl">
      <tbody>
        <tr><td class="cdd-lcell">Witness Name</td><td><CddVarField field-key="witness_name" label="LOI Witness Name" /></td></tr>
        <tr><td class="cdd-lcell">Witness Identification No.</td><td><CddVarField field-key="witness_identification_no" label="LOI Witness ID" /></td></tr>
        <tr><td class="cdd-lcell">Witness Date</td><td><CddVarField field-key="witness_date" label="LOI Witness Date" /></td></tr>
      </tbody>
    </table>

    <div class="sec-title">Authorised Person(s)</div>
    <div class="doc-p doc-note">Each Authorised Person signs to acknowledge the consent under the Agreement.</div>
    <!-- 고객 수정본(2026-10): "SIGNED by [[authorised_person_name]]" (예전 [[ubo_name]] 자리). 비우면 Principal 이름. -->
    <table class="cdd-tbl">
      <tbody>
        <tr><td class="cdd-lcell">SIGNED by (Authorised Person)</td><td><CddVarField field-key="authorised_person_name" label="Authorised Person Name" /></td></tr>
      </tbody>
    </table>
    <p class="doc-p">Signature:</p>
    <AgmSigners var-key="onboard_ap_Signers" board-label="Authorised Persons" />
    <table class="cdd-tbl">
      <tbody>
        <tr><td class="cdd-lcell">Witness (Authorised Person) Name</td><td><CddVarField field-key="authorised_person_witness_name" label="Witness Name" /></td></tr>
        <tr><td class="cdd-lcell">Witness (Authorised Person) ID</td><td><CddVarField field-key="authorised_person_witness_identification_no" label="Witness ID" /></td></tr>
      </tbody>
    </table>

    <div class="sec-title">Schedule 1 &mdash; Directors</div>
    <OnBoardLoopBlock prefix="bnd_dir" :columns="DIR_COLS" add-label="+ Add director" empty-note="No directors." />
    <div class="sec-title">Schedule 1 &mdash; Principal(s)</div>
    <OnBoardLoopBlock prefix="bnd_ubo" :columns="UBO_COLS" add-label="+ Add principal" empty-note="No principals." />
    <div class="sec-title">Schedule 1 &mdash; Shareholder(s)</div>
    <OnBoardLoopBlock prefix="bnd_sh" :columns="SH_COLS" add-label="+ Add shareholder" empty-note="No shareholders." />
  </div>
</template>

<style scoped>
.reg-row { margin: 6px 0; }
.reg-lbl { font-weight: 600; color: #1f2937; margin-bottom: 2px; }
.reg-row .cb-row { display: inline-flex; margin-right: 18px; }
</style>
